'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Shrinks an element's font size, one pixel at a time, until it fits inside its
 * parent box — and only then. The CSS class keeps owning the preferred size;
 * this is the escape hatch for the cases CSS cannot see (an unusually long
 * string, a short landscape card, a verbose locale).
 *
 * Why it exists: the render-progress fact used to be sized by a container-query
 * formula that folded the container's HEIGHT into the font size, so on a
 * phone-sized card it always bottomed out at the 13px floor — a footnote
 * floating in a mostly empty box — even when the fact was two short lines.
 * Guessing "will it fit?" from the box geometry alone cannot work: the answer
 * depends on the text. So we render at the size the design asks for and measure.
 *
 * Mirrors Apple's `minimumScaleFactor` and Android's `TextAutoSize`.
 */
export function useFitText<T extends HTMLElement>(
  /** Re-fit whenever this changes (the rendered string). */
  text: string | undefined,
  minFontSizePx = 12
) {
  const ref = useRef<T>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return;

    // Drop any previous inline size so the class-declared (preferred) size is
    // what we measure from — otherwise a shrink from an earlier, longer fact
    // would stick and never grow back.
    el.style.fontSize = '';
    let size = parseFloat(window.getComputedStyle(el).fontSize);
    if (!Number.isFinite(size)) return;

    while (size > minFontSizePx && el.scrollHeight > box.clientHeight) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
  }, [minFontSizePx]);

  useLayoutEffect(() => {
    fit();
    const box = ref.current?.parentElement;
    if (!box) return undefined;

    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [fit, text]);

  return ref;
}
